import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Backdrop,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatClear,
  TableChart,
  BorderVertical,
  BorderHorizontal,
  DeleteOutline
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { apiClient } from '../../api/apiClient';
import sanitizeHtml from '../../utils/sanitizeHtml';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// Kategorien mit Kürzeln als Werte
const KATEGORIEN = [
  { code: 'R', name: 'Entscheidungen' },
  { code: 'G', name: 'Geschäftsverteilung' },
  { code: 'O', name: 'Organisationsuntersuchung - Extranet' },
  { code: 'V', name: 'Verwaltung' },
  { code: 'S', name: 'Statistik - Extranet' },
  { code: 'E', name: 'Rechtsgrundlagen - Extranet' },
  { code: 'B', name: 'Öffentliche Bekanntmachung gem. § 75 Abs. 2a SGG' },
  { code: 'P', name: 'Pressemitteilungen' }
];

// Bundesländer mit Kürzeln als Werte
const BUNDESLAENDER = [
  { code: "BWB", name: "Baden-Württemberg" },
  { code: "FSB", name: "Bayern" },
  { code: "BLN", name: "Berlin" },
  { code: "BRB", name: "Brandenburg" },
  { code: "BRE", name: "Bremen" },
  { code: "HAM", name: "Hamburg" },
  { code: "HES", name: "Hessen" },
  { code: "MVP", name: "Mecklenburg-Vorpommern" },
  { code: "NSB", name: "Niedersachsen" },
  { code: "NRW", name: "Nordrhein-Westfalen" },
  { code: "RPF", name: "Rheinland-Pfalz" },
  { code: "SAA", name: "Saarland" },
  { code: "FSS", name: "Sachsen" },
  { code: "SAN", name: "Sachsen-Anhalt" },
  { code: "SHS", name: "Schleswig-Holstein" },
  { code: "FST", name: "Thüringen" },
  { code: "BRD", name: "Bundesrepublik Deutschland" }
];

// Editor styles
const editorStyles = {
  '.ProseMirror': {
    minHeight: '150px',
    padding: '1rem',
    backgroundColor: '#fff',
    overflowY: 'auto',
    '&:focus': {
      outline: 'none',
    },
    '& table': {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      width: '100%',
      margin: '0',
      '& td, & th': {
        minWidth: '1em',
        border: '2px solid #ced4da',
        padding: '3px 5px',
        verticalAlign: 'top',
        boxSizing: 'border-box',
        position: 'relative',
        '& > *': {
          margin: 0,
        },
      },
    },
  },
};

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const handleButtonClick = (callback) => (e) => {
    e.preventDefault();
    callback();
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleUnderline().run())}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
        >
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          selected={editor.isActive('orderedList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleOrderedList().run())}
        >
          <FormatListNumbered />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small" exclusive>
        <ToggleButton
          value="left"
          selected={editor.isActive({ textAlign: 'left' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('left').run())}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          selected={editor.isActive({ textAlign: 'center' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('center').run())}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          selected={editor.isActive({ textAlign: 'right' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('right').run())}
        >
          <FormatAlignRight />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="insertTable"
          onMouseDown={handleButtonClick(() => 
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          )}
        >
          <TableChart />
        </ToggleButton>
        <ToggleButton
          value="addColumnBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!editor.can().addColumnBefore()}
        >
          <BorderVertical />
        </ToggleButton>
        <ToggleButton
          value="addRowBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addRowBefore().run())}
          disabled={!editor.can().addRowBefore()}
        >
          <BorderHorizontal />
        </ToggleButton>
        <ToggleButton
          value="deleteTable"
          onMouseDown={handleButtonClick(() => editor.chain().focus().deleteTable().run())}
          disabled={!editor.can().deleteTable()}
        >
          <DeleteOutline />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value="clear"
        onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      >
        <FormatClear />
      </ToggleButton>
    </Box>
  );
};

export default function MitteilungEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [gerichte, setGerichte] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [formData, setFormData] = useState({
    text: '',
    von_datum: new Date(),
    bis_datum: new Date(),
    kategorie: '',
    bundesland: '',
    gericht_id: ''
  });

  const editor = useEditor({
    extensions: [StarterKit, Underline, TextAlign, Table, TableRow, TableHeader, TableCell],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        text: editor.getHTML()
      }));
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Lade Gerichte
        const gerichteResponse = await apiClient.get('/gerichte/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setGerichte(gerichteResponse.data);

        // Lade Mitteilung
        const mitteilungResponse = await apiClient.get(`/mitteilungen/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const mitteilung = mitteilungResponse.data;
        setFormData({
          text: sanitizeHtml(mitteilung.text),
          von_datum: new Date(mitteilung.von_datum),
          bis_datum: new Date(mitteilung.bis_datum),
          kategorie: mitteilung.kategorie,
          bundesland: mitteilung.bundesland,
          gericht_id: mitteilung.gericht_id
        });

        // Lade vorhandene Dateien
        if (mitteilung.files) {
          setExistingFiles(mitteilung.files);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Daten:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Update editor content when formData.text changes
  useEffect(() => {
    if (editor && formData.text !== editor.getHTML()) {
      editor.commands.setContent(formData.text);
    }
  }, [editor, formData.text]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDateForAPI = (date) => {
    return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null;
  };

  const handleDateChange = (name) => (date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (fileToRemove) => {
    setExistingFiles(prevFiles => prevFiles.filter(file => file.storage_filename !== fileToRemove.storage_filename));
  };

  const uploadFiles = async () => {
    const uploadedFiles = [];
    setUploadError(null);
    
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const uploadResponse = await apiClient.post('/files/mitteilungen/upload', formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedFiles.push(uploadResponse.data);
      } catch (error) {
        console.error(`Fehler beim Upload von ${file.name}:`, error);
        
        // Extrahiere die Fehlermeldung vom Backend
        let errorMessage = `Fehler beim Upload von "${file.name}"`;
        
        if (error.response?.status === 400) {
          const backendError = error.response.data?.detail || 'Unbekannter Validierungsfehler';
          
          // Benutzerfreundliche Übersetzung der Backend-Fehlermeldungen
          if (backendError.includes('Nur PDF-Dateien sind erlaubt')) {
            errorMessage = `"${file.name}" wurde abgelehnt: Nur PDF-Dateien sind erlaubt.`;
          } else if (backendError.includes('Datei zu groß')) {
            errorMessage = `"${file.name}" wurde abgelehnt: Die Datei ist zu groß (Maximum: 10 MB).`;
          } else if (backendError.includes('Magic Bytes')) {
            errorMessage = `"${file.name}" wurde abgelehnt: Die Datei ist keine gültige PDF-Datei.`;
          } else if (backendError.includes('Dateiname')) {
            errorMessage = `"${file.name}" wurde abgelehnt: Der Dateiname enthält ungültige Zeichen.`;
          } else if (backendError.includes('MIME-Type')) {
            errorMessage = `"${file.name}" wurde abgelehnt: Ungültiger Dateityp (nur PDF erlaubt).`;
          } else {
            errorMessage = `"${file.name}" wurde abgelehnt: ${backendError}`;
          }
        } else if (error.response?.status === 413) {
          errorMessage = `"${file.name}" wurde abgelehnt: Die Datei ist zu groß.`;
        } else {
          errorMessage = `Fehler beim Upload von "${file.name}": ${error.message}`;
        }
        
        setUploadError(errorMessage);
        throw new Error(errorMessage);
      }
    }
    
    return uploadedFiles;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setUploadError(null);

    try {
      // 1. Neue Dateien hochladen
      let newFileIds = [];
      if (selectedFiles.length > 0) {
        const uploadedFiles = await uploadFiles();
        newFileIds = uploadedFiles.map(file => ({
          storage_filename: file.storage_filename,
          original_filename: file.original_filename,
          file_size: file.file_size
        }));
        
        // Bei erfolgreichem Upload
        if (uploadedFiles.length > 0) {
          setUploadSuccess(true);
          setSelectedFiles([]); // Dateiliste leeren nach erfolgreichem Upload
        }
      }

      // 2. Mitteilung aktualisieren mit allen file_ids (neue + existierende)
      const apiData = {
        ...formData,
        von_datum: formatDateForAPI(formData.von_datum),
        bis_datum: formatDateForAPI(formData.bis_datum),
        kategorie: formData.kategorie,
        bundesland: formData.bundesland,
        file_ids: [
          ...existingFiles.map(file => ({
            storage_filename: file.storage_filename,
            original_filename: file.original_filename,
            file_size: file.file_size
          })),
          ...newFileIds
        ]
      };

      await apiClient.put(`/mitteilungen/${id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      navigate('/admin/mitteilungen');
    } catch (error) {
      console.error('Fehler beim Speichern der Mitteilung:', error);
      
      // Wenn der Fehler nicht vom File Upload kommt, zeige allgemeine Fehlermeldung
      if (!uploadError) {
        setUploadError('Fehler beim Speichern der Mitteilung. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setUploadError(null);
  };

  const handleCloseSuccess = () => {
    setUploadSuccess(false);
  };

  return (
    <Box sx={{ width: '70%', margin: '0 auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mitteilung bearbeiten
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  name="kategorie"
                  value={formData.kategorie}
                  onChange={handleChange}
                  required
                  label="Kategorie"
                >
                  {KATEGORIEN.map((kategorie) => (
                    <MenuItem key={kategorie.code} value={kategorie.code}>
                      {kategorie.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Bundesland</InputLabel>
                <Select
                  name="bundesland"
                  value={formData.bundesland}
                  onChange={handleChange}
                  required
                  label="Bundesland"
                >
                  {BUNDESLAENDER.map((land) => (
                    <MenuItem key={land.code} value={land.code}>
                      {land.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Gericht</InputLabel>
                <Select
                  name="gericht_id"
                  value={formData.gericht_id}
                  onChange={handleChange}
                  required
                  label="Gericht"
                >
                  {gerichte.map((gericht) => (
                    <MenuItem key={gericht.id} value={gericht.id}>
                      {gericht.bezeichnung}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Gültig von"
                value={formData.von_datum}
                onChange={handleDateChange('von_datum')}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Gültig bis"
                value={formData.bis_datum}
                onChange={handleDateChange('bis_datum')}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Text der Mitteilung *
              </Typography>
              <Paper 
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  mb: 2,
                }}
              >
                <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                  <MenuBar editor={editor} />
                </Box>
                <Box sx={editorStyles}>
                  <EditorContent editor={editor} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Angehängte Dateien
              </Typography>

              {/* Upload-Fehler Anzeige */}
              {uploadError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  onClose={handleCloseError}
                >
                  <Typography variant="body2">
                    <strong>Upload-Fehler:</strong><br />
                    {uploadError}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Hinweis: Es sind nur PDF-Dateien bis 10 MB erlaubt.
                  </Typography>
                </Alert>
              )}

              {/* Existierende Dateien */}
              {existingFiles.length > 0 && (
                <List>
                  {existingFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={file.original_filename}
                        secondary={`${(file.file_size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveExistingFile(file)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                >
                  Weitere Dateien auswählen (nur PDF, max. 10 MB)
                  <input
                    type="file"
                    multiple
                    hidden
                    accept=".pdf,application/pdf"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Box>

              {/* Neu ausgewählte Dateien */}
              {selectedFiles.length > 0 && (
                <List>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/mitteilungen')}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Speichern
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={uploadSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          Dateien erfolgreich hochgeladen!
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
} 